import React from "react";

// Texts of the detail tabs of the Google Business Profile audit (Essentials,
// Business info, Social profiles, Reviews, Engagement). The screenshots next
// to the criteria are French Google screenshots in both languages.
const content = {
  fr: {
    googleHl: "fr",
    didYouKnow: "Le saviez-vous ?",
    essentials: {
      title: "Indispensables",
      intro:
        "Les éléments ci-dessous sont les étapes les plus importantes dans l’optimisation de votre fiche Google My Business. Ce sont des facteurs à travailler en priorité pour pouvoir optimiser le reste. Bien suivre les étapes pour avoir une optimisation optimale de votre fiche GMB.",
      claimed: "Établissement revendiqué",
      notClaimed: "Établissement non-revendiqué",
      claimedDesc: (
        <>
          <b>Revendiquez votre fiche Google My Business</b> est la première
          étape pour votre SEO local.
          <br />
          <br />
          Si vous n’avez pas revendiqué votre GMB, vous ne pourrez pas optimiser
          votre fiche. Lorsque vous revendiquez votre fiche GMB, vous pouvez
          modifier vos informations.
          <br />
          <br />
          Si votre fiche n’est pas créé vous devez suivre la procédure pour
          valider votre établissement.
        </>
      ),
      claimedAlt: "établissement revendiqué",
      categoryFound: "Catégorie renseignée",
      categoryMissing: "Catégorie non trouvée",
      mainCategory: (category) => `Votre catégorie principale : ${category}`,
      categoryDesc:
        "Vous êtes obligé d’avoir une catégorie mais vous devez avoir la bonne catégorie principale et les bonnes catégories secondaires.",
      relatedCategories: "Catégories cohérentes avec votre établissement :",
      categoryAlt: "category",
      tipTitle: "Ne pas sur-optimiser le nom de votre établissement",
      tip: (
        <>
          <span>
            Sur-optimiser son nom d’établissement est mal vu par Google, essayez
            de ne pas mettre de mot-clé pour ne pas avoir une fiche suspendue.
          </span>
          <span>
            Lorsque vous mettez un mot clé dans votre nom d’enseigne vous risquez
            de suspendre votre Google my Business et par la même occasion
            empêcher votre fiche d’être référencée.
            <br />
            <br />
            Contrairement à la pensée collective, mettre des mots-clés vous fera
            plus de mal que de bien.
          </span>
        </>
      ),
    },
    informations: {
      title: "Informations",
      intro:
        "Vous devez remplir au maximum les informations sur votre fiche google my business afin d’améliorer votre visibilité. Vos coordonnées doivent être correctes et définitives.",
      address: "Adresse renseignée",
      noAddress: "Adresse non-renseignée",
      addressDesc:
        "Mettre une adresse si vous avez un établissement physique. Google favorise les fiches avec adresse car cela montre que vous êtes un vrai établissement et non un spam.",
      addressAlt: "adresse",
      website: "Site web renseigné",
      noWebsite: "Site web non-renseigné",
      yourWebsite: (url) => `Votre site web renseigné : ${url}`,
      websiteDesc:
        "Vous devez mettre la bonne adresse de votre site web. Ne pas utiliser votre page facebook ou linkedin dans cette section.",
      websiteGoodAlt: "site web good",
      websiteBadAlt: "site web bad",
      hours: "Horaires renseignés",
      noHours: "Horaires non-renseignés",
      hoursDesc: (
        <>
          Vos horaires d’ouvertures et fermetures doivent être visible pour
          informer vos clients de vos disponibilités.
          <br />
          <br />
          Ne vous trompez pas d’heure cela pourrait frustrer certains visiteurs.
        </>
      ),
      hoursAlt: "horaires",
      phone: "Téléphone renseignés",
      noPhone: "Téléphone non-renseigné",
      yourPhone: (phone) => `Votre téléphone renseigné : ${phone}`,
      phoneDesc:
        "Mettez votre numéro de téléphone principal et évitez qu’il soit changé.",
      phoneAlt: "phone",
      description: "Description renseignée",
      noDescription: "Description non-renseignée",
      descriptionDesc: (
        <>
          Ecrivez une petite description pour votre entreprise avec votre mot clé
          principal et votre zone géographique. N’hésitez pas à mettre en avant
          ce qui vous distingue des autres concurrents.
          <br />
          <br />
          Ne pas sur-optimiser votre texte, c’est à dire ne pas répéter plusieurs
          fois vos mots clés.
        </>
      ),
      descriptionAlt: "description renseignée",
    },
    social: {
      title: "Réseaux sociaux",
      intro:
        "Les réseaux sociaux liés à votre fiche Google Business Profile renforcent votre présence en ligne et votre crédibilité. Ils permettent à vos clients de vous retrouver facilement sur leurs plateformes préférées.",
      found: (n) => `Réseaux sociaux détectés (${n})`,
      none: "Aucun réseau social détecté",
      desc: (
        <>
          Ajoutez vos profils de réseaux sociaux à votre fiche Google Business
          Profile pour renforcer votre visibilité.
          <br />
          <br />
          Les plateformes recommandées sont Facebook, Instagram, LinkedIn,
          YouTube et X (Twitter).
          <br />
          <br />
          <b>
            Les liens vers vos réseaux sociaux apparaissent directement sur votre
            fiche dans les résultats de recherche Google.
          </b>
        </>
      ),
    },
    reviews: {
      title: "Avis",
      intro:
        "Les avis sont le nerf de la guerre en SEO local et pour la réputation de votre entreprise. Il a été démontré que les établissements qui génèrent le plus de chiffre d’affaires ont généralement le plus d’avis.",
      enough: "Nombre d'avis minimum correct",
      notEnough: "Nombre d'avis minimum insuffisant",
      countDesc: (
        <>
          Vous devez améliorer votre e-réputation et votre seo local grâce aux
          avis.
          <br />
          <br />
          Avoir des avis vous permet d’avoir plus de visibilité sur Google. Les
          gens prennent de plus en plus une décision d’achat grâce aux avis.
          <br />
          Des études montrent qu’avoir plus d’avis sur Google My Business peut{" "}
          <b>augmenter vos conversions de plus de 20%</b>.
        </>
      ),
      countAlt: "reviews",
      goodRating: "Note moyenne bonne",
      badRating: "Note moyenne à améliorer",
      yourRating: (rating) => (
        <>
          Votre note moyenne est de <b>{rating}</b>
        </>
      ),
      ratingDesc: (
        <>
          <b>Toujours avoir une note supérieure à 4/5.</b>
          <br />
          Il a été démontré qu'avoir une bonne note générale améliorer votre
          taux de clics.
          <br />
          Un établissement avec 5 étoiles recevra plus de clics qu’un
          établissement avec 2 étoiles.
        </>
      ),
      ratingAlt: "rating",
      tipTitle: "Vous pouvez obtenir simplement plus d'avis sur Google.",
      tipSubtitle: "Connaissez-vous le nom de lien court ?",
      tip: (
        <>
          L’objectif de ce lien est d’amener vos clients directement à la section
          avis de votre fiche Google.
          <br />
          <br />
          Cela facilite le parcours pour votre client satisfait et améliore vos
          chances d’avoir un avis.
          <br />
          <br />
          <b>
            Copier votre nom de lien court ci-dessous et partagez-le à vos
            clients !
          </b>
        </>
      ),
      copy: "Copier",
      copied: "Copié !",
      gifAlt: "gif shortlink",
    },
    engagement: {
      title: "Engagement",
      intro:
        "Voici quelques conseils et critères supplémentaires pour optimiser votre fiche Google My Business. Ces éléments sont des nouveautés Google My Business toujours intéressants à utiliser pour dépasser la concurrence.",
      products: "Produit ou service renseigné",
      noProducts: "Aucun produit ou service renseigné",
      productsDesc: (
        <>
          Publiez vos produits et services sur Google. <br />
          Certains établissements peuvent montrer leurs produits ou services avec
          une description et les prix sur les résultats de recherches.
          <br />
          <br />
          Ils seront visibles sous la fiche de l'établissement. Lorsque vous avez
          renseignez vos produits/services, vous améliorez votre taux de clics et
          les conversions sur votre fiche.
        </>
      ),
      productsAlt: "prodcuts",
      posts: "Google Posts récents",
      noPosts: "Aucun Google Post récent",
      postsDesc: (
        <>
          Il est conseillé de publier des Google posts régulièrement. (2 fois par
          mois minimum)
          <br />
          <br />
          Google posts vous permet d'avoir plus de visibilité et informer vos
          clients sur votre actualité.
          <br />
          Ils vous donnent une meilleure visibilité et donne la possibilité
          d'attirer plus de clients dans votre établissement. Les Google posts
          sont visibles sur les résultats de recherches sous votre fiche et
          améliore votre taux de clics.
          <br />
          <br />
          Vos 30 premiers mots doivent être vendeur, le visuel doit respecter les
          consignes Google, mettre un CTA pour inciter les internautes à
          l'action.
        </>
      ),
      postsAlt: "google posts",
      qa: "FAQ renseignée",
      noQa: "Aucune FAQ renseignée",
      qaDesc: (
        <>
          Avoir une FAQ pour votre établissement est très utile pour les
          internautes et peut vous faire gagner un temps précieux.
          <br />
          <br />
          La FAQ permet aux internautes de poser des questions directement à
          l'entreprise. Elle est affichée au public donc il faut faire attention
          à ce que vos clients mettent dessus et bien y répondre.
          <br />
          <br />
          L'entreprise peut aussi créer ses propres questions et y répondre.
          <br />
          <br />
          <b>
            La FAQ, vous évite d'avoir des demandes inutiles par téléphone et
            améliorer votre taux de clics.
          </b>
        </>
      ),
      qaAlt: "FAQ",
    },
  },
  en: {
    googleHl: "en",
    didYouKnow: "Did you know?",
    essentials: {
      title: "Essentials",
      intro:
        "These are the most important steps to optimize your Google Business Profile. Work on them first: everything else builds on them.",
      claimed: "Profile claimed",
      notClaimed: "Profile not claimed",
      claimedDesc: (
        <>
          <b>Claiming your Google Business Profile</b> is the first step of local
          SEO.
          <br />
          <br />
          Until you claim your profile, you can't edit your information or
          optimize anything else.
          <br />
          <br />
          If your business doesn't have a profile yet, create one and complete
          Google's verification process.
        </>
      ),
      claimedAlt: "Claiming a business on Google",
      categoryFound: "Category set",
      categoryMissing: "Category not found",
      mainCategory: (category) => `Your primary category: ${category}`,
      categoryDesc:
        "Every profile has a category, but it needs to be the right primary category, backed by the right additional categories.",
      relatedCategories: "Categories that fit your business:",
      categoryAlt: "Google Business Profile category settings",
      tipTitle: "Don't stuff keywords into your business name",
      tip: (
        <>
          <span>
            Use your real-world business name only. Adding keywords or locations
            to it goes against Google's guidelines and can get your profile
            suspended.
          </span>
          <span>
            {" "}
            A suspended profile disappears from Google Search and Maps until it
            is reinstated.
            <br />
            <br />
            Despite what many think, keywords in your business name will hurt
            you more than they help.
          </span>
        </>
      ),
    },
    informations: {
      title: "Business info",
      intro:
        "Fill in as much information as you can on your Google Business Profile to improve your visibility. Your contact details must be accurate and stable.",
      address: "Address added",
      noAddress: "Address missing",
      addressDesc:
        "If customers can visit you, show your address. Google favors profiles with a real address because it signals a legitimate business, not spam.",
      addressAlt: "Business address on Google",
      website: "Website added",
      noWebsite: "Website missing",
      yourWebsite: (url) => `Your website: ${url}`,
      websiteDesc:
        "Link to your own website (ideally the page for this location). Don't use your Facebook or LinkedIn page as your website.",
      websiteGoodAlt: "Website link on a Google Business Profile",
      websiteBadAlt: "Google Business Profile without a website",
      hours: "Business hours added",
      noHours: "Business hours missing",
      hoursDesc: (
        <>
          Show your opening hours so customers know when they can reach you.
          <br />
          <br />
          Keep them accurate, including holiday hours: showing up to a closed
          store is a frustrating experience.
        </>
      ),
      hoursAlt: "Business hours on Google",
      phone: "Phone number added",
      noPhone: "Phone number missing",
      yourPhone: (phone) => `Your phone number: ${phone}`,
      phoneDesc:
        "Use your main phone number and keep it stable over time: frequent changes confuse customers and Google.",
      phoneAlt: "Phone number on Google",
      description: "Description added",
      noDescription: "Description missing",
      descriptionDesc: (
        <>
          Write a short business description that includes your main service and
          the area you serve, and highlight what sets you apart from
          competitors.
          <br />
          <br />
          Don't over-optimize it: avoid repeating the same keywords over and
          over.
        </>
      ),
      descriptionAlt: "Business description on Google",
    },
    social: {
      title: "Social profiles",
      intro:
        "Social media profiles linked to your Google Business Profile strengthen your online presence and credibility, and help customers find you on the platforms they use.",
      found: (n) => `Social profiles found (${n})`,
      none: "No social profiles found",
      desc: (
        <>
          Add your social media profiles to your Google Business Profile to
          boost your visibility.
          <br />
          <br />
          Recommended platforms: Facebook, Instagram, LinkedIn, YouTube and X
          (Twitter).
          <br />
          <br />
          <b>
            Links to your social profiles appear directly on your profile in
            Google Search results.
          </b>
        </>
      ),
    },
    reviews: {
      title: "Reviews",
      intro:
        "Reviews are at the heart of local SEO and of your business's reputation. The businesses that win locally are usually the ones with the most reviews.",
      enough: "Minimum number of reviews reached",
      notEnough: "Not enough reviews",
      countDesc: (
        <>
          Reviews drive both your local rankings and your reputation.
          <br />
          <br />
          More reviews mean more visibility on Google, and many people check
          reviews before choosing a business.
        </>
      ),
      countAlt: "Number of reviews on Google",
      goodRating: "Good average rating",
      badRating: "Average rating to improve",
      yourRating: (rating) => (
        <>
          Your average rating is <b>{rating}</b>
        </>
      ),
      ratingDesc: (
        <>
          <b>Aim for an average rating of 4 stars or more.</b>
          <br />
          A high rating makes your business look more trustworthy and tends to
          earn more clicks.
          <br />
          Given the choice, people pick a 5-star business over a 2-star one.
        </>
      ),
      ratingAlt: "Star rating on Google",
      tipTitle: "There's an easy way to get more Google reviews.",
      tipSubtitle: "Have you heard of the review short link?",
      tip: (
        <>
          This link takes your customers straight to the review form of your
          Google Business Profile.
          <br />
          <br />
          Fewer steps for happy customers means better odds of getting a
          review.
          <br />
          <br />
          <b>Copy your review link below and share it with your customers!</b>
        </>
      ),
      copy: "Copy",
      copied: "Copied!",
      gifAlt: "Sharing a Google review link",
    },
    engagement: {
      title: "Engagement",
      intro:
        "A few more criteria and tips to optimize your Google Business Profile. These features are worth using to stand out from your competitors.",
      products: "Products or services listed",
      noProducts: "No products or services listed",
      productsDesc: (
        <>
          Publish your products and services on Google. <br />
          Many businesses can show their products or services, with
          descriptions and prices, right in search results.
          <br />
          <br />
          They appear on your profile and help people understand what you offer
          before they click or call.
        </>
      ),
      productsAlt: "Products on a Google Business Profile",
      posts: "Recent Google posts",
      noPosts: "No recent Google posts",
      postsDesc: (
        <>
          Publish Google posts regularly (at least twice a month).
          <br />
          <br />
          Posts keep customers up to date with your news, offers and events.
          <br />
          They show up on your profile in search results and give people one
          more reason to visit or call.
          <br />
          <br />
          Lead with a strong first sentence, use an image that follows Google's
          guidelines and add a call-to-action button.
        </>
      ),
      postsAlt: "Google posts",
      qa: "Q&A filled in",
      noQa: "No Q&A",
      qaDesc: (
        <>
          A Q&A section on your profile helps people and saves you time.
          <br />
          <br />
          It lets people ask your business questions directly on Google.
          Answers are public, so monitor what gets posted and reply carefully.
          <br />
          <br />
          You can also add and answer your own common questions.
          <br />
          <br />
          <b>Good Q&A answers save you phone calls and help people choose you.</b>
        </>
      ),
      qaAlt: "Questions and answers on Google",
    },
  },
};

export default content;
